import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import API_BASE_URL from '../api/Config';

const CategoryContext = createContext();

export const useCategory = () => {
  const context = useContext(CategoryContext);
  if (!context) {
    throw new Error('useCategory must be used within a CategoryProvider');
  }
  return context;
};

export const CategoryProvider = ({ children }) => {
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState({});
  const [productsByCategory, setProductsByCategory] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch all categories
  const fetchCategories = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/categories`, {
        headers: {
          'Accept': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setCategories(data);
      return data;
    } catch (err) {
      setError(err.message);
      console.error('Error fetching categories:', err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Fetch child categories (subcategories) by category ID
  const fetchSubcategories = async (categoryId) => {
    if (!categoryId) return [];
    
    // Return cached data if already fetched
    if (subcategories[categoryId]) {
      return subcategories[categoryId];
    }
    
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/categories/childes/${categoryId}`, {
        headers: {
          'Accept': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Cache subcategories for this category
      setSubcategories(prev => ({
        ...prev,
        [categoryId]: data
      }));
      
      return data;
    } catch (err) {
      setError(err.message);
      console.error('Error fetching subcategories:', err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Get products by category ID with caching
  const fetchProductsByCategory = useCallback(async (categoryId) => {
    if (!categoryId) return [];
    
    // Return cached data if already fetched
    if (productsByCategory[categoryId]) {
      return productsByCategory[categoryId];
    }
    
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/categories/products/${categoryId}`, {
        headers: {
          'Accept': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Cache products for this category
      setProductsByCategory(prev => ({
        ...prev,
        [categoryId]: data
      }));
      
      return data;
    } catch (err) {
      setError(err.message);
      console.error('Error fetching products by category:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, [productsByCategory]);

  // Helper function to get cached subcategories
  const getSubcategories = (categoryId) => {
    return subcategories[categoryId] || [];
  };

  // Helper function to get cached products
  const getCachedProducts = (categoryId) => {
    return productsByCategory[categoryId] || [];
  };

  // Fetch categories on mount
  useEffect(() => {
    fetchCategories().catch(() => {
      // Prevent uncaught promise errors during prerender/build.
    });
  }, []);

  const value = {
    categories,
    subcategories,
    productsByCategory,
    loading,
    error,
    fetchCategories,
    fetchSubcategories,
    fetchProductsByCategory,
    getSubcategories,
    getCachedProducts,
  };

  return (
    <CategoryContext.Provider value={value}>
      {children}
    </CategoryContext.Provider>
  );
};

export default CategoryContext;
